import type { Icon as TablerIcon } from '@tabler/icons-react';
import useTranslation from 'next-translate/useTranslation';

import dt from '~/testing';
import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';
import { Button, LinkButton, UnstyledButton } from '~/domains/design-system/Button';
import IconX from '~/domains/design-system/icons/IconX';
import type { WelcomeModalHeaderType } from '~/domains/common/components/WelcomeModal/WelcomeModalHeader';
import WelcomeModalHeader from '~/domains/common/components/WelcomeModal/WelcomeModalHeader';

import useStyles from './OnboardingWidget.styles';

interface OnboardingWidgetProps {
  title: string;
  subTitle?: string;
  description?: string;
  Icon?: TablerIcon;
  IconSecondary?: TablerIcon;
  eoBrand?: WelcomeModalHeaderType;
  primaryButtonText?: string;
  secondaryButtonText?: string;
  onPrimaryButtonClick?: () => void;
  onSecondaryButtonClick?: () => void;
  isFullScreen?: boolean;
  isWide?: boolean;
  onClose?: () => void;
}
function OnboardingWidget({
  title,
  subTitle,
  description,
  Icon,
  IconSecondary,
  eoBrand,
  primaryButtonText,
  secondaryButtonText,
  onPrimaryButtonClick,
  onSecondaryButtonClick,

  isFullScreen,
  isWide,
  onClose,
}: OnboardingWidgetProps) {
  const { t } = useTranslation('commissioner');
  const { classes, theme } = useStyles({ isFullScreen, isWide });

  return (
    <div
      data-test-id={dt.commissioner.components.onboardingWidget.container}
      className={classes.container}
    >
      {onClose && !isWide && (
        <UnstyledButton className={classes.closeButton} onClick={onClose}>
          <IconX />
        </UnstyledButton>
      )}
      <div className={classes.header}>
        {eoBrand && (
          <div className={classes.eoBrandContainer}>
            <WelcomeModalHeader type={eoBrand} isCardsHidden />
          </div>
        )}
        {Icon && (
          <div className={classes.iconContainer}>
            <Icon size={40} color={theme.colors.blue[3]} stroke={1.5} />
          </div>
        )}
        {IconSecondary && (
          <IconSecondary size={60} color={theme.colors.gray2[2]} stroke={2} />
        )}
      </div>

      <div
        data-test-id={dt.commissioner.components.onboardingWidget.noContests.title}
        className={classes.text}
      >
        {subTitle && (
          <Title variant="subhead-small" transform="uppercase" color={theme.colors.gray[5]}>
            {subTitle}
          </Title>
        )}
        <Title variant={isFullScreen ? 'headline-xxxl' : 'title-x-large'}>{title}</Title>
        <Text
          variant="title-medium"
          color={theme.colors.gray[7]}
          /* eslint-disable-line local-rules/use-theme-tokens */ mt={10}
          tag="div"
        >
          {description}
        </Text>
        {secondaryButtonText && isWide && (
          <LinkButton variant="text" size="medium" onClick={onSecondaryButtonClick}>
            {secondaryButtonText}
          </LinkButton>
        )}
      </div>

      {(primaryButtonText || secondaryButtonText || onClose) && (
        <div className={classes.buttons}>
          {onClose && isWide && (
            <Button
              data-test-id={
                dt.commissioner.components.onboardingWidget.noContests.createMyFirstContest
              }
              variant="text"
              size="medium"
              fullWidth
              onClick={onClose}
            >
              {t('onboardingWidget.dismiss')}
            </Button>
          )}
          {primaryButtonText && (
            <Button
              data-test-id={dt.commissioner.components.onboardingWidget.noContests.learnMore}
              variant="secondary-fill"
              size="medium"
              fullWidth
              onClick={onPrimaryButtonClick}
            >
              {primaryButtonText}
            </Button>
          )}
          {secondaryButtonText && !isWide && (
            <Button
              variant="tertiary-fill"
              size="medium"
              fullWidth
              onClick={onSecondaryButtonClick}
            >
              {secondaryButtonText}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

export default OnboardingWidget;
